import type { CreateContactInquiryRequest, Item } from "@kaidoki/proto";
import {
  CheckCircle,
  MessageSquare,
  Package,
  Plus,
  RefreshCw,
  Settings,
  User,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { purchaseItem, submitContactInquiry } from "../api/client";
import { useAppInit } from "../components/AppInitializer";
import { ItemCard } from "../components/ItemCard";
import { ItemDetailSheet } from "../components/ItemDetailSheet";
import { SuccessFeedback } from "../components/SuccessFeedback";
import { useGroup } from "../contexts/GroupContext";
import { useItemsCache } from "../contexts/ItemsCacheContext";
import { trackGroupSwitch, trackItemPurchase } from "../lib/analytics";
import { getUserInfo } from "../lib/auth";

export function Home() {
  const { t } = useTranslation([
    "home",
    "contact",
    "common",
    "items",
    "templates",
  ]);
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUserInfo();
  const { getItems: getCachedItems, updateDueDate } = useItemsCache();
  const { groups, activeGroupId, setActiveGroup } = useGroup();
  const { fetchAppData } = useAppInit();

  const cachedItems = getCachedItems(activeGroupId);
  const items = cachedItems?.flatMap((d) => (d.item ? [d.item] : [])) ?? [];
  const loading = cachedItems === null;

  const [purchasingIds, setPurchasingIds] = useState<Set<string>>(new Set());
  const [successMessage, setSuccessMessage] = useState<string | null>(() => {
    const state = location.state as { successMessage?: string } | null;
    return state?.successMessage ?? null;
  });

  useEffect(() => {
    const state = location.state as { successMessage?: string } | null;
    if (!state?.successMessage) return;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.search, location.state, navigate]);

  const closeSuccess = useCallback(() => setSuccessMessage(null), []);

  async function handlePurchase(item: Item) {
    if (!activeGroupId || purchasingIds.has(item.id)) return;
    setPurchasingIds((current) => new Set(current).add(item.id));
    try {
      const result = await purchaseItem(activeGroupId, item.id);
      updateDueDate(
        item.id,
        result.nextDueAt,
        result.purchaseEvent,
        activeGroupId,
      );
      trackItemPurchase(item.id);
      setSuccessMessage(
        t("items:purchase.success", {
          name: item.name,
          defaultValue: `「${item.name}」の購入を記録しました`,
        }),
      );
    } catch {
      alert(t("items:errors.purchase"));
    } finally {
      setPurchasingIds((current) => {
        const next = new Set(current);
        next.delete(item.id);
        return next;
      });
    }
  }

  // アクティブグループのキャッシュが未取得なら自動でデータ取得
  useEffect(() => {
    if (activeGroupId && cachedItems === null) {
      fetchAppData().catch(() => {});
    }
  }, [activeGroupId, cachedItems, fetchAppData]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [initialAction, setInitialAction] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);

  // ディープリンク: /home?item=xxx&action=purchase
  useEffect(() => {
    const itemParam = searchParams.get("item");
    const actionParam = searchParams.get("action");
    if (itemParam) {
      setSelectedItemId(itemParam);
      setInitialAction(actionParam);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [category, setCategory] =
    useState<CreateContactInquiryRequest["category"]>("bug");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  const CATEGORIES: {
    value: CreateContactInquiryRequest["category"];
    label: string;
  }[] = [
    { value: "bug", label: t("contact:categories.bug") },
    { value: "feature", label: t("contact:categories.feature") },
    { value: "other", label: t("contact:categories.other") },
  ];

  function handleContactOpen() {
    setContactOpen(true);
  }

  function handleContactClose() {
    setContactOpen(false);
    setCategory("bug");
    setMessage("");
    setSubmitted(false);
    setContactError(null);
  }

  async function handleContactSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim() || submitting) return;

    setSubmitting(true);
    setContactError(null);
    try {
      await submitContactInquiry({ category, message: message.trim() });
      setSubmitted(true);
    } catch (err) {
      setContactError(
        err instanceof Error ? err.message : t("contact:errorFallback"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleGroupSwitch(groupId: string) {
    if (groupId === activeGroupId) return;
    trackGroupSwitch(groupId);
    setActiveGroup(groupId);
  }

  return (
    <div className="min-h-dvh bg-white">
      <header className="sticky top-0 z-20 border-b border-[#e3e8f0] bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-2xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt=""
              className="h-[30px] w-[30px] rounded-[10px]"
            />
            <h1 className="text-lg font-extrabold text-gray-900">
              {t("home:title")}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fetchAppData().catch(() => {})}
              className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-[#e3e8f0] text-gray-600 transition-colors hover:bg-[#f0f3f8]"
              aria-label={t("common:buttons.retry")}
            >
              <RefreshCw size={19} />
            </button>
            <button
              type="button"
              onClick={() => navigate("/settings")}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#e3e8f0] bg-white p-[3px]"
            >
              {user?.pictureUrl ? (
                <img
                  src={user.pictureUrl}
                  alt={user.displayName}
                  className="h-9 w-9 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#edf3ff]">
                  <User size={18} className="text-[#285bbf]" />
                </div>
              )}
            </button>
          </div>
        </div>

        {/* グループ切り替えタブ */}
        {groups.length > 0 && (
          <div className="bg-white pb-2">
            <div className="mx-auto flex max-w-2xl items-center px-4 sm:px-6">
              <div className="min-w-0 flex-1 overflow-x-auto scrollbar-none">
                <div className="flex gap-2 py-1">
                  {groups.map((group) => (
                    <button
                      type="button"
                      key={group.id}
                      onClick={() => handleGroupSwitch(group.id)}
                      className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-bold transition-colors ${
                        group.id === activeGroupId
                          ? "bg-[#edf3ff] text-[#285bbf]"
                          : "bg-[#f0f3f8] text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      {group.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ml-2 flex shrink-0 items-center gap-1">
                {activeGroupId && (
                  <button
                    type="button"
                    onClick={() => navigate(`/groups/${activeGroupId}`)}
                    className="flex h-10 w-10 items-center justify-center rounded-[10px] border border-[#e3e8f0] text-gray-500 hover:bg-[#f0f3f8]"
                  >
                    <Settings size={16} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate("/groups/new")}
                  className="flex h-10 w-10 items-center justify-center rounded-[10px] border border-[#e3e8f0] text-gray-500 hover:bg-[#f0f3f8]"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-2xl px-3 py-2 pb-28 sm:px-6">
        {groups.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-blue-50">
              <Users size={32} className="text-blue-400" />
            </div>
            <h2 className="mb-2 text-base font-semibold text-gray-700">
              {t("home:createGroup.title")}
            </h2>
            <p className="mb-6 text-sm text-gray-500">
              {t("home:createGroup.description")}
            </p>
            <button
              type="button"
              onClick={() => navigate("/groups/new")}
              className="rounded-lg bg-blue-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-600"
            >
              {t("home:createGroup.button")}
            </button>
          </div>
        ) : loading ? (
          <output className="space-y-2" aria-label={t("common:loading")}>
            {[0, 1, 2, 3].map((value) => (
              <div
                key={value}
                className="flex h-[104px] overflow-hidden rounded-[14px] border border-[#e3e8f0] bg-white"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 sm:px-4">
                  <div className="h-11 w-11 shrink-0 rounded-[10px] bg-gray-100 [animation:skeleton-pulse_1.2s_ease-in-out_infinite]" />
                  <div className="min-w-0 flex-1">
                    <div className="h-4 w-2/5 rounded bg-gray-100 [animation:skeleton-pulse_1.2s_ease-in-out_infinite]" />
                    <div className="mt-4 h-3 w-3/5 rounded bg-gray-100 [animation:skeleton-pulse_1.2s_ease-in-out_infinite]" />
                    <div className="mt-4 h-[3px] rounded-full bg-gray-100 [animation:skeleton-pulse_1.2s_ease-in-out_infinite]" />
                  </div>
                </div>
                <div className="flex w-[68px] shrink-0 items-center justify-center">
                  <div className="h-10 w-10 rounded-full bg-gray-100 [animation:skeleton-pulse_1.2s_ease-in-out_infinite]" />
                </div>
              </div>
            ))}
          </output>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-blue-50">
              <Package size={32} className="text-blue-400" />
            </div>
            <h2 className="mb-2 text-base font-semibold text-gray-700">
              {t("home:emptyItems.title")}
            </h2>
            <p className="mb-6 text-sm text-gray-500">
              {t("home:emptyItems.description")}
            </p>
            <button
              type="button"
              onClick={() => navigate("/items/new")}
              className="rounded-lg bg-blue-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-600"
            >
              {t("home:emptyItems.button")}
            </button>
          </div>
        ) : (
          <div>
            <div className="flex flex-col gap-2">
              {items.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  onSelect={setSelectedItemId}
                  onPurchase={handlePurchase}
                  isPurchasing={purchasingIds.has(item.id)}
                />
              ))}
            </div>
          </div>
        )}
      </main>

      {/* お問い合わせ FAB */}
      {!contactOpen && groups.length > 0 && items.length > 0 && (
        <div className="fixed bottom-5 right-4 z-10 flex items-center gap-2 sm:right-6">
          <button
            type="button"
            onClick={handleContactOpen}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#e3e8f0] bg-white text-gray-600 shadow-lg shadow-gray-950/10 transition-transform active:scale-95"
          >
            <MessageSquare size={19} />
          </button>
          <button
            type="button"
            onClick={() => navigate("/items/new")}
            className="flex h-12 items-center justify-center gap-2 rounded-[14px] bg-[#4e81ee] px-5 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition-transform active:scale-[0.98]"
          >
            <Plus size={20} />
            {t("home:addItem")}
          </button>
        </div>
      )}

      {/* アイテム詳細ボトムシート */}
      <ItemDetailSheet
        itemId={selectedItemId}
        initialAction={initialAction}
        onClose={() => {
          setSelectedItemId(null);
          setInitialAction(null);
        }}
        onSuccess={setSuccessMessage}
      />

      <SuccessFeedback
        open={successMessage !== null}
        title={t("common:completed", { defaultValue: "完了しました！" })}
        message={successMessage ?? undefined}
        onClose={closeSuccess}
      />

      {/* お問い合わせパネル */}
      {contactOpen && (
        <>
          <button
            type="button"
            tabIndex={-1}
            className="fixed inset-0 z-20 bg-black/30"
            onClick={handleContactClose}
            aria-label={t("common:buttons.close")}
          />
          <div className="fixed bottom-0 right-0 left-0 z-30 mx-auto max-w-lg rounded-t-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
              <h2 className="text-base font-semibold text-gray-900">
                {t("contact:title")}
              </h2>
              <button
                type="button"
                onClick={handleContactClose}
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="max-h-[70dvh] overflow-y-auto px-4 py-4">
              {submitted ? (
                <div className="flex flex-col items-center gap-4 py-8 text-center">
                  <CheckCircle size={40} className="text-gray-500" />
                  <div>
                    <p className="mb-2 text-[15px] font-semibold text-gray-900">
                      {t("contact:success.title")}
                    </p>
                    <p className="text-[13px] text-gray-500">
                      {t("contact:success.description")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleContactClose}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {t("common:buttons.close")}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  {/* カテゴリ */}
                  <div>
                    <p className="mb-2 text-xs font-medium text-gray-500">
                      {t("contact:categoryLabel")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {CATEGORIES.map((cat) => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => setCategory(cat.value)}
                          className={`rounded-lg border px-3.5 py-2 text-[13px] font-medium transition-colors ${
                            category === cat.value
                              ? "border-gray-900 bg-gray-900 text-white"
                              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* メッセージ */}
                  <div>
                    <label
                      htmlFor="contact-message"
                      className="mb-1 block text-xs font-medium text-gray-500"
                    >
                      {t("contact:content")}
                    </label>
                    <textarea
                      id="contact-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t("contact:placeholder")}
                      rows={4}
                      maxLength={2000}
                      className="w-full resize-none rounded-lg border border-gray-200 p-3 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      required
                    />
                    <p className="mt-1 text-right text-xs text-gray-400">
                      {message.length} / 2000
                    </p>
                  </div>

                  {/* エラー */}
                  {contactError && (
                    <p className="text-[13px] text-red-500">{contactError}</p>
                  )}

                  {/* 送信ボタン */}
                  <button
                    type="submit"
                    disabled={!message.trim() || submitting}
                    className="flex h-11 w-full items-center justify-center rounded-lg bg-blue-500 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-50"
                  >
                    {submitting
                      ? t("common:buttons.submitting")
                      : t("common:buttons.submit")}
                  </button>
                </form>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
